import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  useTheme,
  styled,
  Divider,
  Typography,
  IconButton
} from '@mui/material';
import { Close, Edit, Delete, AddPhotoAlternate } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

import CategoryDetails from './CategoryDetails';
import CategoryList from './CategoryList';
import EditCategoryForm from './EditCategoryForm';
import AddCategoryForm from './AddCategoryForm';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    minWidth: '60vw',
    [theme.breakpoints.down('md')]: {
      minWidth: '90vw'
    }
  }
}));

const SmallStyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    width: '450px', // Fixed width for small dialog
    maxWidth: 'none', // Override maxWidth
    [theme.breakpoints.down('sm')]: {
      width: '90vw' // Responsive for mobile
    }
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    fontWeight: 600
  }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`
}));

const CategoryInfoModal = ({ open, onClose, category }) => {
  const theme = useTheme();
  const [categoryNames, setCategoryNames] = useState([]);
  const [loading, setLoading] = useState(false);

  // New category state
  const [newCategory, setNewCategory] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newShortCode, setNewShortCode] = useState('');
  const [imageFile, setImageFile] = useState(null);

  // Edit category state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  const [editCategorySlug, setEditCategorySlug] = useState('');
  const [editShortCode, setEditShortCode] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);

  useEffect(() => {
    if (open && category) {
      setLoading(true);
      axios
        .get(`/api/v1/categories/get-categories?type_id=${category.id}`)
        .then((response) => {
          setCategoryNames(response.data.data[0]?.category_names || []);
        })
        .catch((error) => {
          toast.error('Error fetching category names');
          console.error('Error fetching category names:', error);
        })
        .finally(() => setLoading(false));
    }
  }, [open, category]);

  const resetFormStates = () => {
    setNewCategory('');
    setNewSlug('');
    setNewShortCode('');
    setImageFile(null);
    setEditingCategoryId(null);
    setEditCategoryValue('');
    setEditCategorySlug('');
    setEditShortCode('');
    setEditImageFile(null);
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      toast.warning('Please enter a category name');
      return;
    }

    const formData = new FormData();
    formData.append('value', newCategory);
    formData.append('slug', newSlug);
    formData.append('category_type_id', category.id);
    formData.append('folder', 'categories');
    formData.append('shortCode', newShortCode);
    if (imageFile) formData.append('image', imageFile);

    setLoading(true);
    axios
      .post('/api/v1/categories-name/create', formData)
      .then((res) => {
        toast.success(res.data.message || 'Category added successfully');
        setCategoryNames((prev) => [...prev, res.data.data]);
        resetFormStates();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Error adding category');
      })
      .finally(() => setLoading(false));
  };

  const handleEditCategory = (id, value, slug, shortCode) => {
    setEditingCategoryId(id);
    setEditCategoryValue(value);
    setEditCategorySlug(slug || '');
    setEditShortCode(shortCode || '');
    setEditImageFile(null);
    setEditModalOpen(true);
  };

  const handleUpdateCategory = () => {
    if (!editCategoryValue.trim() || !editingCategoryId) {
      toast.warning('Please enter a valid category name');
      return;
    }

    const formData = new FormData();
    formData.append('value', editCategoryValue);
    formData.append('slug', editCategorySlug);
    formData.append('category_type_id', category.id);
    formData.append('folder', 'categories');
    formData.append('shortCode', editShortCode);
    if (editImageFile) formData.append('image', editImageFile);

    setLoading(true);
    axios
      .put(`/api/v1/categories-name/update/${editingCategoryId}`, formData)
      .then((res) => {
        toast.success(res.data.message || 'Category updated successfully');
        setCategoryNames((prev) =>
          prev.map((item) =>
            item.id === editingCategoryId
              ? {
                  ...item,
                  value: editCategoryValue,
                  slug: editCategorySlug,
                  shortCode: editShortCode
                }
              : item
          )
        );
        resetFormStates();
        setEditModalOpen(false);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Error updating category');
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      axios
        .delete(`/api/v1/categories-name/delete/${id}`)
        .then((res) => {
          toast.success(res.data.message || 'Category deleted successfully');
          setCategoryNames((prev) => prev.filter((item) => item.id !== id));
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || 'Error deleting category');
        });
    }
  };

  const existingImageUrl =
    categoryNames.find((cat) => cat.id === editingCategoryId)?.image_url || '';

  return (
    <>
      {/* Main Category Info Modal */}
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          <Typography variant="h6">Category Details</Typography>
          <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </StyledDialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {category ? (
            <>
              <CategoryDetails category={category} />
              
              <Divider sx={{ my: 3 }} />

              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: 200
                }}>
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight="600" mb={2}>
                    Subcategories ({categoryNames.length})
                  </Typography>

                  <CategoryList
                    categoryNames={categoryNames}
                    onEdit={handleEditCategory}
                    onDelete={handleDeleteCategory}
                  />

                  <Typography variant="subtitle1" fontWeight="600" mt={4} mb={2}>
                    Add New Subcategory
                  </Typography>
                  <AddCategoryForm
                    value={newCategory}
                    onChange={setNewCategory}
                    slug={newSlug}
                    setSlug={setNewSlug}
                    shortCode={newShortCode}
                    setShortCode={setNewShortCode}
                    imageFile={imageFile}
                    setImageFile={setImageFile}
                    onSubmit={handleAddCategory}
                  />
                </>
              )}
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: 200
            }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        
        <StyledDialogActions>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{
              borderRadius: theme.shape.borderRadius,
              textTransform: 'none',
              px: 3
            }}
          >
            Close
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Edit Subcategory Modal - Smaller Width */}
      <SmallStyledDialog 
        open={editModalOpen} 
        onClose={() => setEditModalOpen(false)}
      >
        <StyledDialogTitle>
          <Typography variant="h6">Edit Subcategory</Typography>
          <IconButton onClick={() => setEditModalOpen(false)} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </StyledDialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <EditCategoryForm
            value={editCategoryValue}
            setValue={setEditCategoryValue}
            slug={editCategorySlug}
            setSlug={setEditCategorySlug}
            shortCode={editShortCode}
            setShortCode={setEditShortCode}
            imageFile={editImageFile}
            setImageFile={setEditImageFile}
            existingImageUrl={existingImageUrl}
            onUpdate={handleUpdateCategory}
            onCancel={() => setEditModalOpen(false)}
          />
        </DialogContent>
      </SmallStyledDialog>
    </>
  );
};

export default CategoryInfoModal;